import { STRUCK_OUT_STATES, NOT_REVIEWED, CONFIG_REMOVED, AUTO_CONFIG_REMOVED } from './inventory/reviewStates';

/**
 * Review-status sort for the Bug and CVE tables (STRY0038457).
 *
 * Ascending puts the crossed-out rows first, then the rows a reviewer has
 * decided something about but kept, then the untouched ones:
 *
 *   struck out → reviewed, still in play → Not reviewed → anything unrecognised
 *
 * so the table reads "done, in progress, not started". Descending reverses the
 * groups, which puts the work still to do at the top. The struck group follows
 * STRUCK_OUT_STATES, the same list that decides the strike-through, so the
 * rows this sort lifts to the top are exactly the struck rows.
 */
const IN_PLAY_STATES = ['Confirmed', 'Workaround identified', 'Escalated for review', 'Uncertain'];

const RANKED_STATES = [...STRUCK_OUT_STATES, ...IN_PLAY_STATES, NOT_REVIEWED];

const UNKNOWN_RANK = RANKED_STATES.length;

export const STATUS_SORT_ASC = 'asc';
export const STATUS_SORT_DESC = 'desc';

export function reviewStatusRank(state) {
  // Both config-removed flavours share a rank. Opening an auto-marked row's
  // status control seeds the human flavour before anything is saved, and a
  // rank change there would move the row out from under the open control.
  const rank = RANKED_STATES.indexOf(state === AUTO_CONFIG_REMOVED ? CONFIG_REMOVED : state);
  return rank === -1 ? UNKNOWN_RANK : rank;
}

/**
 * A sorted copy of `items`. Stable: rows of the same state keep the order the
 * API sent them in, in both directions, so flipping the sort never shuffles a
 * group internally.
 *
 * @param items      the filtered bugs or CVEs
 * @param stateOf    item → the review state the row displays (effectiveState)
 * @param direction  STATUS_SORT_ASC, STATUS_SORT_DESC, or anything else for no sort
 */
export function sortByReviewStatus(items, stateOf, direction) {
  if (!Array.isArray(items)) return [];
  if (direction !== STATUS_SORT_ASC && direction !== STATUS_SORT_DESC) return items;
  const sign = direction === STATUS_SORT_DESC ? -1 : 1;
  return items
    .map((item, index) => ({ item, index, rank: reviewStatusRank(stateOf(item)) }))
    .sort((a, b) => sign * (a.rank - b.rank) || a.index - b.index)
    .map(entry => entry.item);
}

/** Header click cycle: off → crossed-out first → crossed-out last → off. */
export function nextStatusSort(direction) {
  if (direction === STATUS_SORT_ASC) return STATUS_SORT_DESC;
  if (direction === STATUS_SORT_DESC) return null;
  return STATUS_SORT_ASC;
}

export function ariaSortFor(direction) {
  if (direction === STATUS_SORT_ASC) return 'ascending';
  if (direction === STATUS_SORT_DESC) return 'descending';
  return 'none';
}
